import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Clock, Zap, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { appTemplates } from '../templates';
import api from '../utils/api';

const FEATURED_IDS = ['chatbot', 'code-assistant', 'image-generator', 'content-generator', 'social-media-manager', 'data-analyzer'];

const GRADIENTS = {
  blue: 'from-blue-600/30 via-blue-800/20 to-slate-900',
  green: 'from-emerald-600/30 via-emerald-800/20 to-slate-900',
  purple: 'from-purple-600/30 via-purple-800/20 to-slate-900',
  indigo: 'from-indigo-600/30 via-indigo-800/20 to-slate-900',
  yellow: 'from-amber-600/30 via-amber-800/20 to-slate-900',
  pink: 'from-pink-600/30 via-pink-800/20 to-slate-900',
};

const POPULARITY = {
  chatbot: { stars: 4.9, uses: '12.4K' },
  'code-assistant': { stars: 4.9, uses: '22.3K' },
  'image-generator': { stars: 4.8, uses: '15.1K' },
  'content-generator': { stars: 4.7, uses: '8.2K' },
  'social-media-manager': { stars: 4.7, uses: '11.5K' },
  'data-analyzer': { stars: 4.5, uses: '6.8K' },
};

const FeaturedTemplates = () => {
  const navigate = useNavigate();
  const featured = appTemplates.filter(t => FEATURED_IDS.includes(t.id));

  const handleUse = (template) => {
    api.post('/templates/track', { template_id: template.id, template_name: template.name }).catch(() => {});
    navigate('/new-project', {
      state: { template: { name: template.name, description: template.description, style: 'modern', stack: template.stack, aiModel: template.aiModel } }
    });
  };

  return (
    <div className="p-6" data-testid="featured-templates-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-4.5 h-4.5 text-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Featured Templates</h2>
            <p className="text-xs text-muted-foreground">Start building faster with pre-built AI apps</p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => navigate('/templates')}
          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 text-sm gap-1"
          data-testid="view-all-templates-btn"
        >
          View All <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {featured.map(t => {
          const pop = POPULARITY[t.id] || { stars: 4.5, uses: '1K' };
          const grad = GRADIENTS[t.color] || GRADIENTS.blue;
          return (
            <Card
              key={t.id}
              className="bg-card border-border hover:border-blue-500/30 transition-all group cursor-pointer overflow-hidden"
              data-testid={`featured-template-${t.id}`}
              onClick={() => handleUse(t)}
            >
              <div className={`relative h-24 bg-gradient-to-br ${grad} flex items-center justify-center`}>
                <span className="text-4xl transform group-hover:scale-110 transition-transform">{t.icon}</span>
                <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/40 backdrop-blur-sm">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span className="text-xs text-foreground font-medium">{pop.stars}</span>
                </div>
                <div className="absolute top-2 left-2">
                  <Badge className={`text-xs ${
                    t.complexity === 'Easy' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                    t.complexity === 'Medium' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                    'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>{t.complexity}</Badge>
                </div>
              </div>
              <CardContent className="p-3.5">
                <h3 className="text-foreground font-semibold text-sm group-hover:text-blue-400 transition-colors mb-1">{t.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{t.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.estimatedTime}</span>
                  <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{pop.uses} uses</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturedTemplates;
